import type { ArticulatedNote } from "./types";
import type { Instrument, InstrumentVariant } from "../data/instruments";
import {
  createOmbakPairFrequencies,
  createOudCourseFrequencies,
  daegeumCheongBuzzAmount,
  tablaBayanPressureBend,
  tablaBayanReleaseFall
} from "./pitch";
import { createFilteredNoise } from "./noise";
import { applyEnvelope, releaseEnvelope } from "./envelope";
import { MIN_GAIN, type AudioVoice } from "./constants";
import { canRenderAdvanced, renderAdvanced } from "./models/modelRouter";

export interface PlaySampleNoteOptions {
  id: string;
  context: BaseAudioContext;
  frequency: number;
  velocity: number;
  waveform?: OscillatorType;
  buffer?: AudioBuffer;
  recordedHz?: number;
  attack?: number;
  decay?: number;
  sustain?: number;
  release?: number;
  filterFrequency?: number;
  q?: number;
}

export function playSampleNote(options: PlaySampleNoteOptions): AudioVoice {
  const {
    id,
    context,
    frequency,
    velocity,
    waveform = "triangle",
    buffer,
    recordedHz,
    attack = 0.006,
    decay = 0.18,
    sustain = 0.32,
    release = 0.42,
    filterFrequency = 2800,
    q = 0.9
  } = options;
  const source = buffer ? context.createBufferSource() : context.createOscillator();
  const gain = context.createGain();
  const filter = context.createBiquadFilter();

  if (source instanceof AudioBufferSourceNode) {
    source.buffer = buffer ?? null;
    source.playbackRate.value = recordedHz ? frequency / recordedHz : 1;
  } else {
    source.type = waveform;
    source.frequency.setValueAtTime(frequency, context.currentTime);
  }

  filter.type = "lowpass";
  filter.frequency.value = filterFrequency;
  filter.Q.value = q;
  gain.gain.value = MIN_GAIN;

  source.connect(filter);
  filter.connect(gain);
  applyEnvelope(gain.gain, context, {
    attack,
    decay,
    sustain,
    release,
    peakGain: Math.max(0.03, velocity * 0.45)
  });
  source.start();

  return {
    id,
    output: gain,
    gain,
    sources: [source],
    cleanupNodes: [filter],
    stop: (when = context.currentTime) => {
      releaseEnvelope(gain.gain, context, release, when);
      source.stop(when + release + 0.06);
    }
  };
}

export interface RenderVoiceOptions {
  bowPressure: number;
  bowSpeed: number;
  breathPressure: number;
  cheongTension: number;
  cheongExposure: number;
  buzzAmount: number;
  bayanPressure: number;
  neyMicrobend: number;
  ombakDepth: number;
  saronTuningHint: "genericSlendroApprox" | "genericPelogApprox";
  tremolo?: boolean;
  nonghyeonDepth?: number;
  microBendAmount?: number;
  meendMode?: "beginner" | "standard" | "expert";
  mouthShapeY?: number;
  circularHold?: boolean;
  vocalization?: number;
}

export class SampleEngine {
  render(
    context: BaseAudioContext,
    note: ArticulatedNote,
    instrument: Instrument,
    variant: InstrumentVariant,
    options: RenderVoiceOptions
  ): AudioVoice {
    if (canRenderAdvanced(instrument.id)) {
      const advanced = renderAdvanced({
        context,
        destination: context.createGain(),
        instrumentId: instrument.id,
        variantId: variant.id,
        frequency: note.frequency,
        velocity: note.velocity,
        duration: note.release + note.decay + 0.8,
        noteLabel: note.noteName,
        ornament: note.ornament,
        controls: {
          ...options,
          noteBrightness: note.brightness,
          noteResonance: note.resonance,
          tuningId: note.tuning.id,
          tuningApproximation: note.tuning.isApproximation
        },
        now: context.currentTime,
        quality: "standard"
      });

      if (advanced) {
        return advanced;
      }
    }

    if (instrument.id === "gayageum") {
      return this.renderGayageum(context, note, variant, options);
    }

    if (instrument.id === "oud") {
      return this.renderOud(context, note, variant, options);
    }

    if (instrument.id === "sitar") {
      return this.renderSitar(context, note);
    }

    if (instrument.id === "erhu") {
      return this.renderErhu(context, note, variant, options);
    }

    if (instrument.id === "daegeum" || instrument.id === "ney") {
      return this.renderBreathInstrument(context, note, instrument, variant, options);
    }

    if (instrument.id === "janggu" || instrument.id === "tabla") {
      return this.renderDrum(context, note, instrument, variant, options);
    }

    if (instrument.id === "mbira") {
      return this.renderMbira(context, note, options);
    }

    if (instrument.id === "gamelan_saron") {
      return this.renderSaron(context, note, options);
    }

    if (instrument.id === "kora") {
      return this.renderKora(context, note);
    }

    return playSampleNote({
      id: note.id,
      context,
      frequency: note.frequency,
      velocity: note.velocity,
      waveform: instrument.id === "kora" ? "triangle" : "triangle",
      attack: note.attack,
      decay: note.decay,
      sustain: note.sustain,
      release: note.release,
      filterFrequency: instrument.id === "gayageum" ? 3200 : 2600,
      q: instrument.id === "gayageum" ? 1.6 : 0.9
    });
  }

  private renderGayageum(
    context: BaseAudioContext,
    note: ArticulatedNote,
    variant: InstrumentVariant,
    options: RenderVoiceOptions
  ): AudioVoice {
    const isModern = variant.id === "modern25" || variant.id === "modern21";
    const body = playSampleNote({
      id: note.id,
      context,
      frequency: note.frequency,
      velocity: note.velocity,
      waveform: isModern ? "triangle" : "sawtooth",
      attack: 0.002,
      decay: note.decay,
      sustain: isModern ? 0.26 : 0.2,
      release: note.release,
      filterFrequency: isModern ? 4600 : 3300,
      q: isModern ? 1.5 : 2.3
    });
    
    const now = context.currentTime;
    const filter = body.cleanupNodes[0] as BiquadFilterNode;
    // Add realistic string pluck filter sweep (fast decay of high frequencies)
    filter.frequency.setValueAtTime(isModern ? 4500 : 3200, now);
    filter.frequency.exponentialRampToValueAtTime(isModern ? 800 : 600, now + 0.4);

    const pluckNoise = createFilteredNoise(context, isModern ? 5200 : 3900, 3.4, 0.05);
    const pluckGain = context.createGain();
    const woodResonance = context.createBiquadFilter();
    const bridgePartial = context.createOscillator();
    const bridgeGain = context.createGain();
    
    // Add slight random detune for human feel (불균일성)
    const randomDetune = (Math.random() - 0.5) * 6;
    if (body.sources[0] instanceof OscillatorNode) {
      body.sources[0].detune.value = randomDetune;
    }

    pluckGain.gain.setValueAtTime(note.velocity * (isModern ? 0.04 : 0.06), now);
    pluckGain.gain.exponentialRampToValueAtTime(MIN_GAIN, now + 0.06);

    woodResonance.type = "peaking";
    woodResonance.frequency.value = isModern ? 620 : 430;
    woodResonance.Q.value = isModern ? 1.5 : 2.4;
    woodResonance.gain.value = isModern ? 2.4 : 4.2;

    bridgePartial.type = "sine";
    bridgePartial.frequency.value = note.frequency * (isModern ? 2.01 : 1.98);
    bridgePartial.detune.value = randomDetune;
    
    // Natural partial decay
    bridgeGain.gain.setValueAtTime(note.velocity * (isModern ? 0.03 : 0.05), now);
    bridgeGain.gain.exponentialRampToValueAtTime(MIN_GAIN, now + (isModern ? 0.6 : 0.4));

    pluckNoise.filter.connect(pluckGain);
    pluckGain.connect(body.gain);
    bridgePartial.connect(bridgeGain);
    bridgeGain.connect(woodResonance);
    woodResonance.connect(body.gain);
    pluckNoise.source.start(now);
    bridgePartial.start(now);

    if (options.tremolo) {
      const source = body.sources[0];
      if (source instanceof OscillatorNode) {
        source.detune.linearRampToValueAtTime(12 + randomDetune, now + 0.04);
        source.detune.linearRampToValueAtTime(-10 + randomDetune, now + 0.08);
        source.detune.linearRampToValueAtTime(8 + randomDetune, now + 0.12);
      }
    }

    body.sources.push(pluckNoise.source, bridgePartial);
    body.cleanupNodes.push(pluckNoise.filter, pluckGain, woodResonance, bridgeGain);
    const stopBody = body.stop;
    body.stop = (when = context.currentTime) => {
      stopBody(when);
      bridgePartial.stop(when + note.release + 0.08);
    };

    return body;
  }

  private renderKora(context: BaseAudioContext, note: ArticulatedNote): AudioVoice {
    const panValue = note.ornament === "left_thumb" ? -0.35 : note.ornament === "right_index" ? 0.35 : 0;
    const body = playSampleNote({
      id: note.id,
      context,
      frequency: note.frequency,
      velocity: note.velocity,
      waveform: "triangle",
      attack: 0.003,
      decay: 0.24,
      sustain: 0.34,
      release: 0.86,
      filterFrequency: 3600,
      q: 1.15
    });
    
    const now = context.currentTime;
    const filter = body.cleanupNodes[0] as BiquadFilterNode;
    // Fast filter sweep removes "synth harp" sustained high end, giving a woody pluck
    filter.frequency.setValueAtTime(3800, now);
    filter.frequency.exponentialRampToValueAtTime(1200, now + 0.4);

    const calabash = context.createBiquadFilter();
    const skinNoise = createFilteredNoise(context, 1200, 1.1, 0.18);
    const skinGain = context.createGain();
    const panner = context.createStereoPanner();

    calabash.type = "peaking";
    calabash.frequency.value = 260;
    calabash.Q.value = 1.4;
    calabash.gain.value = 3.3;
    
    skinGain.gain.setValueAtTime(note.velocity * 0.032, now);
    skinGain.gain.exponentialRampToValueAtTime(MIN_GAIN, now + 0.1); // Short slap on skin
    
    panner.pan.value = panValue;

    skinNoise.filter.connect(skinGain);
    skinGain.connect(calabash);
    calabash.connect(body.gain);
    body.output.connect(panner);
    skinNoise.source.start(now);

    body.output = panner;
    body.sources.push(skinNoise.source);
    body.cleanupNodes.push(calabash, skinNoise.filter, skinGain, panner);

    return body;
  }

  private renderOud(
    context: BaseAudioContext,
    note: ArticulatedNote,
    variant: InstrumentVariant,
    options: RenderVoiceOptions
  ): AudioVoice {
    const detune: [number, number] =
      variant.id === "turkish_oud" ? [-2, 4] : variant.id === "iraqi_oud" ? [-4, 3] : [-3, 2];
    const [lower, upper] = createOudCourseFrequencies({
      courseId: `${variant.id}-course`,
      stringCount: 2,
      baseHz: note.frequency,
      detuneCentsPair: detune
    });
    const sources = [context.createOscillator(), context.createOscillator()];
    const gain = context.createGain();
    const filter = context.createBiquadFilter();
    const bodyResonance = context.createBiquadFilter();
    const now = context.currentTime;

    sources[0].type = "triangle";
    sources[1].type = "triangle";
    sources[0].frequency.value = lower;
    sources[1].frequency.value = upper;
    
    // Warm, woody filter sweep
    const baseFreq = variant.id === "turkish_oud" ? 3800 : variant.id === "iraqi_oud" ? 3200 : 2600;
    filter.type = "lowpass";
    filter.frequency.setValueAtTime(baseFreq, now);
    filter.frequency.exponentialRampToValueAtTime(800, now + 0.15); // Fast drop to body warmth
    filter.Q.value = variant.id === "iraqi_oud" ? 1.8 : 1.1;
    
    bodyResonance.type = "peaking";
    bodyResonance.frequency.value = 240; // Oud body resonance
    bodyResonance.Q.value = 1.3;
    bodyResonance.gain.value = 3.5;

    gain.gain.value = MIN_GAIN;

    for (const source of sources) {
      source.connect(filter);
      source.start(now);
    }

    if (options.tremolo) {
      for (const source of sources) {
        source.detune.setValueAtTime(0, now);
        source.detune.linearRampToValueAtTime(8, now + 0.04);
        source.detune.linearRampToValueAtTime(-6, now + 0.08);
        source.detune.linearRampToValueAtTime(5, now + 0.12);
      }
    }

    filter.connect(bodyResonance);
    bodyResonance.connect(gain);
    
    // Softer attack than guitar, round woody decay
    applyEnvelope(gain.gain, context, {
      attack: 0.006,
      decay: variant.id === "arabic_oud" ? 0.22 : 0.16,
      sustain: 0.25,
      release: variant.id === "iraqi_oud" ? 0.45 : 0.35,
      peakGain: note.velocity * (variant.id === "turkish_oud" ? 0.34 : 0.42)
    });

    return {
      id: note.id,
      output: gain,
      gain,
      sources,
      cleanupNodes: [filter, bodyResonance],
      stop: (when = context.currentTime) => {
        releaseEnvelope(gain.gain, context, 0.36, when);
        for (const source of sources) {
          source.stop(when + 0.46);
        }
      }
    };
  }

  private renderSitar(context: BaseAudioContext, note: ArticulatedNote): AudioVoice {
    const body = playSampleNote({
      id: note.id,
      context,
      frequency: note.frequency,
      velocity: note.velocity,
      waveform: "sawtooth",
      attack: 0.004,
      decay: 0.22,
      sustain: 0.24,
      release: 0.7,
      filterFrequency: 4900,
      q: 2.7
    });
    
    const now = context.currentTime;
    const filter = body.cleanupNodes[0] as BiquadFilterNode;
    
    // Jivari jawari effect: Descending formants (energy flow to lower partials)
    filter.type = "peaking";
    filter.Q.value = 6.5;
    filter.gain.value = 14;
    filter.frequency.setValueAtTime(8500, now); // Start extremely high and buzzy
    filter.frequency.exponentialRampToValueAtTime(4200, now + 0.12);
    filter.frequency.exponentialRampToValueAtTime(1200, now + 0.65);

    const buzz = createFilteredNoise(context, note.frequency * 5.2, 6, 0.7);
    const buzzGain = context.createGain();
    
    buzzGain.gain.setValueAtTime(note.velocity * 0.055, now);
    buzzGain.gain.exponentialRampToValueAtTime(MIN_GAIN, now + 0.4);

    // Multiple sympathetic strings (tarafdar)
    const sympatheticFrequencies = [2.01, 3.01, 4.02];
    const sympatheticGains = [0.06, 0.03, 0.015];
    const sympatheticSources: OscillatorNode[] = [];
    const sympatheticGainNodes: GainNode[] = [];

    sympatheticFrequencies.forEach((multiplier, i) => {
      const sym = context.createOscillator();
      const sGain = context.createGain();
      sym.type = "sine";
      sym.frequency.value = note.frequency * multiplier;
      
      // Long ringing decay for sympathetic strings
      sGain.gain.setValueAtTime(note.velocity * sympatheticGains[i], now);
      sGain.gain.exponentialRampToValueAtTime(MIN_GAIN, now + 1.2);
      
      sym.connect(sGain);
      sGain.connect(body.gain);
      sym.start(now);
      
      sympatheticSources.push(sym);
      sympatheticGainNodes.push(sGain);
    });

    buzz.filter.connect(buzzGain);
    buzzGain.connect(body.gain);
    buzz.source.start(now);

    body.sources.push(buzz.source, ...sympatheticSources);
    body.cleanupNodes.push(buzz.filter, buzzGain, ...sympatheticGainNodes);
    
    const stopBody = body.stop;
    body.stop = (when = context.currentTime) => {
      stopBody(when);
      sympatheticSources.forEach(s => s.stop(when + 1.2));
    };

    return body;
  }

  private renderErhu(
    context: BaseAudioContext,
    note: ArticulatedNote,
    variant: InstrumentVariant,
    options: RenderVoiceOptions
  ): AudioVoice {
    const shapeBrightness =
      variant.soundbox?.shape === "octagonal" ? 220 : variant.soundbox?.shape === "rare_circular" ? -160 : 0;
    
    // Soft attack for bow drawing
    const attackTime = Math.max(0.05, 0.12 - options.bowSpeed * 0.05);
    const body = playSampleNote({
      id: note.id,
      context,
      frequency: note.frequency,
      velocity: note.velocity * (0.8 + options.bowPressure * 0.4),
      waveform: "sawtooth",
      attack: attackTime,
      decay: 0.2 + (1 - options.bowSpeed) * 0.1,
      sustain: 0.85,
      release: 0.4,
      filterFrequency: 1800 + options.bowPressure * 2400 + shapeBrightness,
      q: 1.2
    });

    const now = context.currentTime;
    
    // Nasal formant filter
    const nasalFormant = context.createBiquadFilter();
    nasalFormant.type = "peaking";
    nasalFormant.frequency.value = 1100 + options.bowPressure * 400;
    nasalFormant.Q.value = 4.0;
    nasalFormant.gain.value = 12; // Strong nasal resonance
    
    // Reroute body output through nasal formant
    body.output.disconnect();
    body.output.connect(nasalFormant);
    body.output = nasalFormant;
    body.cleanupNodes.push(nasalFormant);

    // Bow friction noise (slightly rough)
    const bowNoise = createFilteredNoise(context, 1600 + options.bowPressure * 2000, 1.8, 1.5);
    const noiseGain = context.createGain();
    
    noiseGain.gain.setValueAtTime(0, now);
    noiseGain.gain.linearRampToValueAtTime(options.bowPressure * 0.05, now + attackTime);
    noiseGain.gain.setTargetAtTime(options.bowPressure * 0.03, now + attackTime + 0.1, 0.2);

    bowNoise.filter.connect(noiseGain);
    noiseGain.connect(body.gain);
    bowNoise.source.start(now);

    const source = body.sources[0];
    if (source instanceof OscillatorNode) {
      // Add pitch instability and continuous vibrato
      const vibrato = context.createOscillator();
      const vibratoGain = context.createGain();
      
      vibrato.frequency.value = 5.2 + Math.random() * 1.5; // Natural vibrato rate
      vibratoGain.gain.setValueAtTime(0, now); // Delay vibrato
      vibratoGain.gain.linearRampToValueAtTime(14 + options.bowPressure * 8, now + attackTime + 0.2); // Cent wobble
      
      vibrato.connect(vibratoGain);
      vibratoGain.connect(source.detune);
      vibrato.start(now);
      
      body.sources.push(vibrato);
      body.cleanupNodes.push(vibratoGain);
    }

    body.sources.push(bowNoise.source);
    body.cleanupNodes.push(bowNoise.filter, noiseGain);

    return body;
  }

  private renderBreathInstrument(
    context: BaseAudioContext,
    note: ArticulatedNote,
    instrument: Instrument,
    variant: InstrumentVariant,
    options: RenderVoiceOptions
  ): AudioVoice {
    const isPersianNey = variant.id === "persian_ney";
    const cheongBuzz =
      instrument.id === "daegeum"
        ? daegeumCheongBuzzAmount({
            breathPressure: options.breathPressure,
            cheongTension: options.cheongTension,
            cheongExposure: options.cheongExposure
          })
        : 0;
    
    const attackTime = 0.07;
    const body = playSampleNote({
      id: note.id,
      context,
      frequency: note.frequency,
      velocity: note.velocity * (0.65 + options.breathPressure * 0.35),
      waveform: "sine", // Base sine for air column
      attack: attackTime,
      decay: 0.15,
      sustain: 0.8,
      release: 0.35,
      filterFrequency:
        isPersianNey
          ? 4200 + options.neyMicrobend * 280
          : 2400 + options.breathPressure * 1400 + options.neyMicrobend * 160,
      q: isPersianNey ? 1.7 : 0.9
    });

    const now = context.currentTime;
    const sustainedBuzzSources: OscillatorNode[] = [];
    
    // Add organic triangle overtone to prevent "organ/sine" sound
    const bodyOvertone = context.createOscillator();
    const overtoneGain = context.createGain();
    bodyOvertone.type = "triangle";
    bodyOvertone.frequency.value = note.frequency;
    
    overtoneGain.gain.setValueAtTime(0, now);
    overtoneGain.gain.linearRampToValueAtTime(note.velocity * 0.15, now + attackTime * 1.5);
    overtoneGain.gain.setTargetAtTime(note.velocity * 0.1, now + attackTime + 0.1, 0.3);
    
    // Flutter modulation for breath jitter
    const breathLfo = context.createOscillator();
    const breathLfoGain = context.createGain();
    breathLfo.type = "sine";
    breathLfo.frequency.value = instrument.id === "ney" ? 14 : 18;
    breathLfoGain.gain.value = 0.04 + options.breathPressure * 0.04;
    
    breathLfo.connect(breathLfoGain);
    breathLfoGain.connect(overtoneGain.gain);
    breathLfo.start(now);
    
    bodyOvertone.connect(overtoneGain);
    overtoneGain.connect(body.gain);
    bodyOvertone.start(now);
    
    body.sources.push(bodyOvertone, breathLfo);
    body.cleanupNodes.push(overtoneGain, breathLfoGain);

    if (instrument.id === "ney") {
      // Hollow notch for Ney's empty tube sound
      const hollowNotch = context.createBiquadFilter();
      hollowNotch.type = "notch";
      hollowNotch.frequency.value = 1400;
      hollowNotch.Q.value = 1.2;
      
      body.output.disconnect();
      body.output.connect(hollowNotch);
      body.output = hollowNotch;
      body.cleanupNodes.push(hollowNotch);
    }

    const breath = createFilteredNoise(context, isPersianNey ? 3600 : 2200, 0.6, 1.1);
    const breathGain = context.createGain();
    const edge = instrument.id === "ney" ? createFilteredNoise(context, isPersianNey ? 5200 : 4100, 3.2, 1.1) : null;
    const edgeGain = edge ? context.createGain() : null;

    breathGain.gain.value = options.breathPressure * 0.08;
    breath.filter.connect(breathGain);
    breathGain.connect(body.gain);
    breath.source.start(now);
    body.sources.push(breath.source);
    body.cleanupNodes.push(breath.filter, breathGain);

    if (edge && edgeGain) {
      edgeGain.gain.value = (0.015 + options.breathPressure * 0.03) * (isPersianNey ? 1.4 : 1);
      edge.filter.connect(edgeGain);
      edgeGain.connect(body.gain);
      edge.source.start(now);
      body.sources.push(edge.source);
      body.cleanupNodes.push(edge.filter, edgeGain);
    }

    const source = body.sources[0];
    if (source instanceof OscillatorNode) {
      const instability = instrument.id === "ney" ? 12 + options.neyMicrobend * 30 : 6 + options.breathPressure * 15;
      source.detune.setValueAtTime(0, now);
      source.detune.linearRampToValueAtTime(instability, now + attackTime + 0.05);
      source.detune.linearRampToValueAtTime(-instability * 0.5, now + 0.3);
      source.detune.linearRampToValueAtTime(0, now + 0.5);
    }

    if (cheongBuzz > 0) {
      const buzz = context.createOscillator();
      const buzzShaper = context.createWaveShaper();
      const buzzFilter = context.createBiquadFilter();
      const buzzGain = context.createGain();
      
      // Non-linear threshold curve for Cheong (tanh)
      const curve = new Float32Array(256);
      for (let i = 0; i < 256; i++) {
        const x = (i * 2) / 255 - 1;
        curve[i] = Math.tanh(x * (3 + cheongBuzz * 8)); // Hard clip when blown hard
      }
      buzzShaper.curve = curve;
      buzzShaper.oversample = "2x";
      
      buzz.type = "sine"; // Start pure, let shaper create harmonics
      buzz.frequency.value = note.frequency; 
      
      buzzFilter.type = "bandpass";
      buzzFilter.frequency.value = 3800 + cheongBuzz * 1200;
      buzzFilter.Q.value = 3.5;

      buzzGain.gain.setValueAtTime(0, now);
      buzzGain.gain.linearRampToValueAtTime(cheongBuzz * 0.22, now + attackTime);
      
      buzz.connect(buzzShaper);
      buzzShaper.connect(buzzFilter);
      buzzFilter.connect(buzzGain);
      buzzGain.connect(body.gain);
      buzz.start(now);
      
      body.sources.push(buzz);
      body.cleanupNodes.push(buzzShaper, buzzFilter, buzzGain);
      sustainedBuzzSources.push(buzz);
    }

    const stopBody = body.stop;
    body.stop = (when = context.currentTime) => {
      stopBody(when);
      for (const source of sustainedBuzzSources) {
        source.stop(when + 0.35);
      }
    };

    return body;
  }

  private renderDrum(
    context: BaseAudioContext,
    note: ArticulatedNote,
    instrument: Instrument,
    variant: InstrumentVariant,
    options: RenderVoiceOptions
  ): AudioVoice {
    const isTabla = instrument.id === "tabla";
    const isLowHead = note.ornament === "gung" || note.ornament === "bayan_pressure";
    const now = context.currentTime;
    
    const baseFrequency = isTabla
      ? (isLowHead ? 120 : 260)
      : (isLowHead ? 145 : 380);

    const gain = context.createGain();
    gain.gain.value = MIN_GAIN;

    const sources: AudioScheduledSourceNode[] = [];
    const cleanupNodes: AudioNode[] = [];

    // Body Tone
    const bodyOsc = context.createOscillator();
    const bodyGain = context.createGain();
    bodyOsc.type = isTabla ? "sine" : "triangle";
    bodyOsc.frequency.value = baseFrequency;
    
    if (isTabla && isLowHead) {
      // Bayan pressure bend
      const pressedHz = tablaBayanPressureBend(baseFrequency, options.bayanPressure || 0.55);
      bodyOsc.frequency.linearRampToValueAtTime(pressedHz, now + 0.08);
      bodyOsc.frequency.linearRampToValueAtTime(
        tablaBayanReleaseFall(pressedHz, baseFrequency, 1),
        now + 0.32
      );
    }

    bodyGain.gain.setValueAtTime(0, now);
    bodyGain.gain.linearRampToValueAtTime(note.velocity * 0.8, now + 0.005);
    bodyGain.gain.exponentialRampToValueAtTime(MIN_GAIN, now + (isLowHead ? 0.6 : 0.3));

    bodyOsc.connect(bodyGain);
    bodyGain.connect(gain);
    bodyOsc.start(now);
    sources.push(bodyOsc);
    cleanupNodes.push(bodyGain);

    // Transient & Overtones
    if (isTabla && !isLowHead) {
      // Tabla Dayan syahi rings (Raman 1:2:3:4 near-harmonic alignment)
      const syahiPartials = [2.01, 2.9, 4.02];
      const syahiGains = [0.4, 0.25, 0.15];
      const syahiDecays = [0.4, 0.25, 0.15]; // Higher partials decay faster
      
      syahiPartials.forEach((multiplier, i) => {
        const ringOsc = context.createOscillator();
        const ringGain = context.createGain();
        ringOsc.type = "sine";
        ringOsc.frequency.value = baseFrequency * multiplier;
        
        ringGain.gain.setValueAtTime(0, now);
        ringGain.gain.linearRampToValueAtTime(note.velocity * syahiGains[i], now + 0.002);
        ringGain.gain.exponentialRampToValueAtTime(MIN_GAIN, now + syahiDecays[i]);
        
        ringOsc.connect(ringGain);
        ringGain.connect(gain);
        ringOsc.start(now);
        sources.push(ringOsc);
        cleanupNodes.push(ringGain);
      });
      
      // Sharp finger transient
      const click = createFilteredNoise(context, 4500, 1.2, 0.02);
      const clickGain = context.createGain();
      clickGain.gain.setValueAtTime(note.velocity * 0.3, now);
      clickGain.gain.exponentialRampToValueAtTime(MIN_GAIN, now + 0.02);
      click.filter.connect(clickGain);
      clickGain.connect(gain);
      click.source.start(now);
      sources.push(click.source);
      cleanupNodes.push(click.filter, clickGain);
    } else if (!isTabla) {
      // Janggu transient & wood slap
      const slapNoise = createFilteredNoise(context, isLowHead ? 1200 : 3800, 1.5, 0.1);
      const slapGain = context.createGain();
      slapGain.gain.setValueAtTime(note.velocity * (isLowHead ? 0.35 : 0.65), now);
      slapGain.gain.exponentialRampToValueAtTime(MIN_GAIN, now + (isLowHead ? 0.05 : 0.1));
      
      slapNoise.filter.connect(slapGain);
      slapGain.connect(gain);
      slapNoise.source.start(now);
      sources.push(slapNoise.source);
      cleanupNodes.push(slapNoise.filter, slapGain);
    }

    // Master Envelope for safety
    applyEnvelope(gain.gain, context, {
      attack: 0.001,
      decay: 0.1,
      sustain: 1.0,
      release: 0.1,
      peakGain: 1.0
    });

    return {
      id: note.id,
      output: gain,
      gain,
      sources,
      cleanupNodes,
      stop: (when = context.currentTime) => {
        releaseEnvelope(gain.gain, context, 0.05, when);
        sources.forEach(s => s.stop(when + 0.1));
      }
    };
  }

  private renderMbira(context: BaseAudioContext, note: ArticulatedNote, options: RenderVoiceOptions): AudioVoice {
    const body = playSampleNote({
      id: note.id,
      context,
      frequency: note.frequency,
      velocity: note.velocity,
      waveform: "triangle",
      attack: 0.003,
      decay: 0.26,
      sustain: 0.18,
      release: 0.84,
      filterFrequency: 5600,
      q: 2.6
    });
    
    const now = context.currentTime;
    
    // Bottlecap buzz
    const buzz = createFilteredNoise(context, note.frequency * 8, 8, 0.8);
    const buzzGain = context.createGain();
    buzzGain.gain.setValueAtTime(options.buzzAmount * 0.07, now);
    buzzGain.gain.exponentialRampToValueAtTime(MIN_GAIN, now + 0.4); // Buzz decays faster than note
    
    buzz.filter.connect(buzzGain);
    buzzGain.connect(body.gain);
    buzz.source.start(now);
    
    // Tine transient click
    const click = createFilteredNoise(context, 7200, 7, 0.05);
    const clickGain = context.createGain();
    clickGain.gain.setValueAtTime(note.velocity * 0.12, now);
    clickGain.gain.exponentialRampToValueAtTime(MIN_GAIN, now + 0.03); // Very short click
    
    // Primary lamella inharmonic overtone (African Mbira ~5.1x)
    const overtone = context.createOscillator();
    const overtoneGain = context.createGain();
    overtone.type = "sine";
    overtone.frequency.value = note.frequency * 5.1; 
    overtoneGain.gain.setValueAtTime(note.velocity * 0.12, now); // Stronger presence
    overtoneGain.gain.exponentialRampToValueAtTime(MIN_GAIN, now + 0.7);
    
    // High secondary inharmonic overtone (~13.8x)
    const overtoneHigh = context.createOscillator();
    const overtoneHighGain = context.createGain();
    overtoneHigh.type = "sine";
    overtoneHigh.frequency.value = note.frequency * 13.8;
    overtoneHighGain.gain.setValueAtTime(note.velocity * 0.05, now);
    overtoneHighGain.gain.exponentialRampToValueAtTime(MIN_GAIN, now + 0.35);

    click.filter.connect(clickGain);
    clickGain.connect(body.gain);
    
    overtone.connect(overtoneGain);
    overtoneGain.connect(body.gain);
    
    overtoneHigh.connect(overtoneHighGain);
    overtoneHighGain.connect(body.gain);
    
    click.source.start(now);
    overtone.start(now);
    overtoneHigh.start(now);
    
    body.sources.push(buzz.source, click.source, overtone, overtoneHigh);
    body.cleanupNodes.push(buzz.filter, buzzGain, click.filter, clickGain, overtoneGain, overtoneHighGain);
    
    const stopBody = body.stop;
    body.stop = (when = context.currentTime) => {
      stopBody(when);
      overtone.stop(when + 0.9);
      overtoneHigh.stop(when + 0.9);
    };

    return body;
  }

  private renderSaron(context: BaseAudioContext, note: ArticulatedNote, options: RenderVoiceOptions): AudioVoice {
    const tuningOffset = options.saronTuningHint === "genericPelogApprox" ? 4 : 0;
    const { lower, upper } = createOmbakPairFrequencies(note.frequency, 4 + options.ombakDepth * 20 + tuningOffset);
    const sources = [
      context.createOscillator(),
      context.createOscillator(),
      context.createOscillator(),
      context.createOscillator()
    ];
    const gain = context.createGain();
    const filter = context.createBiquadFilter();
    const strikeNoise = createFilteredNoise(context, 2400, 2.4, 0.06);
    const strikeGain = context.createGain();
    const now = context.currentTime;

    sources[0].type = "sine";
    sources[1].type = "sine";
    sources[2].type = "sine";
    sources[3].type = "sine";
    sources[0].frequency.value = lower;
    sources[1].frequency.value = upper;
    sources[2].frequency.value = note.frequency * 2.71; // Inharmonic partial 1
    sources[3].frequency.value = note.frequency * 4.08; // Inharmonic partial 2
    
    filter.type = "peaking";
    filter.frequency.value = note.frequency * 2.4;
    filter.Q.value = 12;
    filter.gain.value = 7;
    
    strikeGain.gain.setValueAtTime(note.velocity * 0.07, now);
    strikeGain.gain.exponentialRampToValueAtTime(MIN_GAIN, now + 0.04); // Fast metallic strike
    
    gain.gain.value = MIN_GAIN;

    // Separate envelopes for high partials
    const highPartialGain = context.createGain();
    highPartialGain.gain.setValueAtTime(1.0, now);
    highPartialGain.gain.exponentialRampToValueAtTime(MIN_GAIN, now + 0.6); // Fast decay for high ringing

    sources[0].connect(filter);
    sources[1].connect(filter);
    sources[2].connect(highPartialGain);
    sources[3].connect(highPartialGain);
    highPartialGain.connect(filter);
    
    for (const source of sources) {
      source.start(now);
    }
    
    strikeNoise.filter.connect(strikeGain);
    strikeGain.connect(filter);
    filter.connect(gain);
    strikeNoise.source.start(now);
    
    // Envelope for fundamental ombak pair
    applyEnvelope(gain.gain, context, {
      attack: 0.002,
      decay: 0.38,
      sustain: 0.36,
      release: 1.2,
      peakGain: note.velocity * 0.44
    });

    return {
      id: note.id,
      output: gain,
      gain,
      sources,
      cleanupNodes: [filter, strikeNoise.filter, strikeGain, highPartialGain],
      stop: (when = context.currentTime) => {
        releaseEnvelope(gain.gain, context, 0.055, when);
        for (const source of sources) {
          source.stop(when + 0.12);
        }
      }
    };
  }
}
